import React, { useState, useEffect } from 'react';
import { api } from '../App';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Plus, Edit2, Trash2, ClipboardList, Search } from 'lucide-react';
import { Badge } from '../components/ui/badge';

export default function TaskMaster() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({ name: '', description: '' });

  useEffect(() => { fetchTasks(); }, []);

  const fetchTasks = async () => {
    try {
      const r = await api.get('/task-master');
      setTasks(r.data);
    } catch { toast.error('Failed to fetch tasks'); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) { toast.error('Task name is required'); return; }
    try {
      if (selected) {
        await api.put(`/task-master/${selected.id}`, formData);
        toast.success('Task updated');
      } else {
        await api.post('/task-master', formData);
        toast.success('Task created');
      }
      setDialogOpen(false);
      resetForm();
      fetchTasks();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/task-master/${selected.id}`);
      toast.success('Task deleted');
      setDeleteDialogOpen(false);
      setSelected(null);
      fetchTasks();
    } catch { toast.error('Failed to delete'); }
  };

  const toggleActive = async (task) => {
    try {
      await api.put(`/task-master/${task.id}`, { is_active: !task.is_active });
      setTasks(prev => prev.map(t => t.id === task.id ? { ...t, is_active: !t.is_active } : t));
      toast.success(task.is_active ? 'Task deactivated' : 'Task activated');
    } catch { toast.error('Failed to toggle'); }
  };

  const resetForm = () => { setFormData({ name: '', description: '' }); setSelected(null); };

  const openEdit = (task) => {
    setSelected(task);
    setFormData({ name: task.name, description: task.description || '' });
    setDialogOpen(true);
  };

  const filtered = tasks.filter(t =>
    t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (t.description || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6 animate-fade-in" data-testid="task-master-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-slate-800">Task Master</h1>
          <p className="text-slate-500">Create and manage daily activity tasks</p>
        </div>
        <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="bg-[#FF9933] hover:bg-[#E68A2E]" data-testid="add-task-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Task
        </Button>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input placeholder="Search tasks..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" data-testid="task-search" />
      </div>

      <Card className="bg-white border shadow-sm">
        <CardContent className="p-0">
          <Table data-testid="task-master-table">
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-semibold w-12">#</TableHead>
                <TableHead className="font-semibold">Task Name</TableHead>
                <TableHead className="font-semibold">Description</TableHead>
                <TableHead className="font-semibold">Status</TableHead>
                <TableHead className="font-semibold text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-500">Loading...</TableCell></TableRow>
              ) : filtered.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-500">
                  <ClipboardList className="w-8 h-8 mx-auto mb-2 text-slate-300" />No tasks found. Create your first task.
                </TableCell></TableRow>
              ) : filtered.map((task, idx) => (
                <TableRow key={task.id} data-testid={`task-row-${task.id}`}>
                  <TableCell className="text-slate-500 font-mono">{idx + 1}</TableCell>
                  <TableCell className="font-medium">{task.name}</TableCell>
                  <TableCell className="text-slate-600 text-sm">{task.description || '-'}</TableCell>
                  <TableCell>
                    <button onClick={() => toggleActive(task)} className="cursor-pointer" data-testid={`toggle-task-${task.id}`}>
                      <Badge className={task.is_active ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-red-100 text-red-600 hover:bg-red-200'}>
                        {task.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </button>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(task)} className="text-slate-600 hover:text-[#FF9933]" data-testid={`edit-task-${task.id}`}>
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => { setSelected(task); setDeleteDialogOpen(true); }} className="text-slate-600 hover:text-red-500" data-testid={`delete-task-${task.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{selected ? 'Edit Task' : 'Add New Task'}</DialogTitle>
            <DialogDescription>{selected ? 'Update task details' : 'Create a new master task for daily activities'}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Task Name *</Label>
              <Input value={formData.name} onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))} required placeholder="e.g. Office Cleaning" data-testid="task-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input value={formData.description} onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))} placeholder="Brief description of the task" data-testid="task-desc-input" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-[#FF9933] hover:bg-[#E68A2E]" data-testid="save-task-btn">{selected ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Task</DialogTitle>
            <DialogDescription>Delete "{selected?.name}"? This will also remove it from all employee assignments.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-task">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
